import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_THUMB =
  "https://images.unsplash.com/photo-1478720568477-152d9b164e26?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function MyVideos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState(null);

  useEffect(() => {
    if (user === undefined) return;
    if (user === null) return navigate("/connexion", { state: { from: "/mes-videos" } });
    api.get("/my/videos").then((r) => setVideos(r.data)).catch(() => setVideos([]));
  }, [user, navigate]);

  return (
    <div className="bg-luxe min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 lg:pt-40 pb-16">
        <h1 className="font-display text-3xl lg:text-4xl text-[#F5F3EE]">Mes vidéos</h1>
        <p className="mt-3 text-sm text-[#8F8F8F]">Retrouvez ici toutes les vidéos que vous avez achetées.</p>

        {!videos ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
          </div>
        ) : videos.length === 0 ? (
          <div data-testid="no-purchases" className="text-center py-24">
            <p className="text-[#8F8F8F]">Vous n'avez pas encore de vidéo.</p>
            <Link
              to="/"
              data-testid="browse-catalogue-link"
              className="inline-block mt-6 bg-[#D4AF37] text-[#0A0A0A] font-semibold px-8 py-3.5 text-sm uppercase tracking-widest hover:bg-[#C9A227] transition-colors"
            >
              Découvrir le catalogue
            </Link>
          </div>
        ) : (
          <div data-testid="my-videos-grid" className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {videos.map((v) => (
              <Link
                key={v.id}
                to={`/video/${v.id}`}
                data-testid={`my-video-card-${v.id}`}
                className="group block bg-[#141414] border border-white/10 hover:border-[#D4AF37]/60 hover:shadow-[0_0_28px_rgba(212,175,55,0.14)] transition-[border-color,box-shadow] duration-300"
              >
                <div className="aspect-video overflow-hidden bg-[#1A1A1A] relative">
                  <img
                    src={v.thumbnail_url || DEFAULT_THUMB}
                    alt={v.title}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <PlayCircle className="w-14 h-14 text-white" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl text-[#F5F3EE] leading-snug">{v.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#D4AF37]">
                    <PlayCircle className="w-4 h-4" /> Regarder
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
